import React, { useState, useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaHome,
  FaUtensils,
  FaClipboardList,
  FaUserCircle,
  FaArrowLeft,
  FaPlus,
  FaTimes,
  FaCaretDown,
} from "react-icons/fa";
import {
  fetchCaptains,
  setCurrentCaptain,
  createCaptain,
} from "../../utils/api";

import AuthContext from "../../contexts/AuthContext";
import HomeScreen from "./homeScreen";
import MenusScreen from "./menusScreen";
import OrdersScreen from "./ordersScreen";
import ProfileScreen from "./profileScreen";

export default function Home() {
  const location = useLocation();
  const navigate = useNavigate();
  const initial = location?.state?.initial || null;
  const [active, setActive] = useState(initial);
  const { restaurantId } = useContext(AuthContext);

  // Captain state management
  const [captains, setCaptains] = useState([]);
  const [currentCaptainId, setCurrentCaptainId] = useState(null);
  const [loadingCaptains, setLoadingCaptains] = useState(false);
  const [captainModalOpen, setCaptainModalOpen] = useState(false);
  const [newCaptainName, setNewCaptainName] = useState("");
  const [showCaptainDropdown, setShowCaptainDropdown] = useState(false);
  const [isOrderScreenOpen, setIsOrderScreenOpen] = useState(false);
  // Keep bottom navigation always visible on mobile (unless an order screen explicitly hides it)
  const [showBottomNav] = useState(true);

  useEffect(() => {
    async function loadCaptains() {
      if (!restaurantId) return;

      setLoadingCaptains(true);
      try {
        const caps = await fetchCaptains(restaurantId);
        setCaptains(Array.isArray(caps) ? caps : []);
      } catch (err) {
        console.warn("Failed to load captains:", err);
        setCaptains([]);
      }
      setLoadingCaptains(false);
    }

    loadCaptains();
  }, [restaurantId]);

  // Set current captain on server
  async function handleSetCurrentCaptain(capId) {
    if (!restaurantId) return;
    try {
      await setCurrentCaptain(restaurantId, capId || null);
      setCurrentCaptainId(capId || null);
      setShowCaptainDropdown(false);
    } catch (err) {
      console.error("Failed to set current captain:", err);
      alert("Failed to set captain");
    }
  }

  // Create new captain
  async function handleCreateCaptain() {
    if (!restaurantId) {
      alert("No restaurant id");
      return;
    }
    if (!newCaptainName.trim()) return;
    try {
      const res = await createCaptain(restaurantId, newCaptainName.trim());
      // Refresh captains list
      const caps = await fetchCaptains(restaurantId);
      setCaptains(Array.isArray(caps) ? caps : []);
      setNewCaptainName("");
      setCaptainModalOpen(false);
      // Automatically select the newly created captain
      if (res && res.id) {
        await handleSetCurrentCaptain(res.id);
      }
    } catch (err) {
      console.error("Failed to create captain:", err);
      alert("Failed to create captain");
    }
  }

  const renderContent = () => {
    if (!active) return <div className="h-full w-full" aria-hidden="true" />;
    switch (active) {
      case "Home":
        return <HomeScreen />;
      case "Menus":
        return <MenusScreen />;
      case "Orders":
        return (
          <OrdersScreen
            restaurantId={restaurantId}
            onOrderScreenOpen={setIsOrderScreenOpen}
          />
        );
      case "Profile":
        return <ProfileScreen />;
      default:
        return <div className="h-full" />;
    }
  };

  const navItems = [
    { key: "Home", label: "Dashboard", icon: <FaHome /> },
    { key: "Menus", label: "Menu", icon: <FaUtensils /> },
    { key: "Orders", label: "Orders", icon: <FaClipboardList /> },
    { key: "Profile", label: "Profile", icon: <FaUserCircle /> },
  ];

  // Get current captain name
  const currentCaptain = captains.find((c) => c.id === currentCaptainId);
  const currentCaptainName = currentCaptain
    ? currentCaptain.name
    : "Unassigned";

  // Only show back arrow and captain selector on Home screen
  const showTopBarControls = active === "Home";

  return (
    /* Put mobile frame near top and make height responsive so header is never cut on small phones */
    <div className="w-full min-h-screen flex justify-center bg-gradient-to-br mt-6 p-4">
      {/* Mobile container */}
      <div
        // Allow modals/overlays to render above the container (prevent clipping)
        className="relative w-[390px] bg-white dark:bg-slate-900 shadow-xl rounded-3xl overflow-visible"
        style={{ height: "calc(100vh - 3rem)", maxHeight: "844px" }}
      >
        {/* Top bar with back arrow and captain selector - Only show on Home screen */}
        {showTopBarControls && (
          <div className="absolute top-0 left-0 right-0 h-16 bg-white dark:bg-slate-800 border-b dark:border-slate-700 flex items-center justify-between px-4 z-20">
            {/* Back arrow */}
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
              aria-label="Go back to admin panel"
            >
              <FaArrowLeft className="text-gray-600 dark:text-gray-300" />
            </button>

            {/* Captain selector - on the right side */}
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold px-4 py-2 text-gray-700 dark:text-gray-300">
                Captain
              </h3>
              <div className="relative">
                <button
                  onClick={() => setShowCaptainDropdown(!showCaptainDropdown)}
                  className="flex items-center gap-1 px-3 py-1 rounded-md bg-rose-100 text-rose-700 font-semibold text-sm"
                >
                  <span>{currentCaptainName}</span>
                  <FaCaretDown />
                </button>

                {/* Captain dropdown */}
                {showCaptainDropdown && (
                  <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-800 rounded-md shadow-lg border border-gray-200 dark:border-slate-700 z-50 overflow-hidden">
                    <div className="py-1 max-h-40 overflow-y-auto">
                      <button
                        onClick={() => handleSetCurrentCaptain(null)}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          !currentCaptainId
                            ? "bg-rose-100 text-rose-700 dark:bg-slate-700 dark:text-white"
                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                        }`}
                      >
                        Unassigned
                      </button>

                      {captains.map((captain) => (
                        <button
                          key={captain.id}
                          onClick={() => handleSetCurrentCaptain(captain.id)}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            currentCaptainId === captain.id
                              ? "bg-rose-100 text-rose-700 dark:bg-slate-700 dark:text-white"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                          }`}
                        >
                          {captain.name}
                        </button>
                      ))}
                    </div>

                    <div className="border-t border-gray-200 dark:border-slate-700">
                      <button
                        onClick={() => {
                          setShowCaptainDropdown(false);
                          setCaptainModalOpen(true);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2"
                      >
                        <FaPlus className="text-xs" />
                        Add New Captain
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Content area */}
        <main className="h-full pb-24 overflow-auto pt-16 w-full">
          <div className="min-h-full w-full">{renderContent()}</div>
        </main>

        {/* Bottom nav stays fixed for mobile (Orders screen can still hide it via isOrderScreenOpen) */}
        {!isOrderScreenOpen && (
          <nav
            /* lowered z so app-level modals with higher z will appear above nav */
            className="absolute left-6 right-6 z-30 translate-y-0 transition-transform duration-300"
            style={{ bottom: "1rem" }}
            role="navigation"
            aria-label="Bottom Navigation"
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg px-3 py-2 flex justify-between items-center">
              {navItems.map((n) => {
                const isActive = active === n.key;
                return (
                  <button
                    key={n.key}
                    onClick={() => setActive(isActive ? null : n.key)}
                    className="flex-1 flex flex-col items-center gap-1 py-2 px-1 focus:outline-none"
                    aria-current={isActive ? "page" : undefined}
                    aria-label={n.label}
                  >
                    <div
                      className={`w-11 h-11 flex items-center justify-center rounded-lg transition-all duration-150 ${
                        isActive
                          ? "bg-rose-500 text-white shadow-md"
                          : "bg-white text-gray-600"
                      }`}
                    >
                      <span className="text-lg">{n.icon}</span>
                    </div>
                    <span
                      className={`text-xs mt-1 transition-opacity duration-150 ${
                        isActive ? "opacity-100" : "opacity-50"
                      }`}
                    >
                      {n.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>
        )}

        {/* Add Captain Modal - Only for mobile devices */}
        {captainModalOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4"
            onClick={() => setCaptainModalOpen(false)}
          >
            <div
              className="bg-white dark:bg-slate-800 rounded-lg p-4 sm:p-6 w-full max-w-sm relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 p-1"
                onClick={() => setCaptainModalOpen(false)}
              >
                <FaTimes className="text-lg" />
              </button>
              <h3 className="text-lg font-bold mb-4 dark:text-white">
                Add Captain
              </h3>
              <div className="mb-3">
                <label className="block text-sm font-semibold mb-1 dark:text-gray-300">
                  Name
                </label>
                <input
                  type="text"
                  className="border dark:border-slate-600 px-3 py-2 rounded w-full focus:outline-none focus:ring-2 focus:ring-rose-300 bg-white dark:bg-slate-700 dark:text-white text-sm sm:text-base"
                  value={newCaptainName}
                  onChange={(e) => setNewCaptainName(e.target.value)}
                  placeholder="Enter captain name"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreateCaptain}
                  className="flex-1 w-full py-2 rounded font-medium text-white bg-gradient-to-r from-rose-400 to-pink-500 hover:from-rose-500 hover:to-pink-600"
                >
                  Save
                </button>
                <button
                  onClick={() => setCaptainModalOpen(false)}
                  className="py-2 px-4 rounded font-medium border"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
